// ArticleCard.tsx (web + MUI) - fixed layout
import React, { memo } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const CARD_H = 130;
const LABEL_H = 34; // tweak as desired
const CONTROLS_H = 34; // tweak as desired

const ArticleCard = memo(
  ({ article, setSelection, count }: { article: any; setSelection: (change: { [id: number]: number }) => void; count: number }) => {
    const imageUrl = article?.image && import.meta.env.VITE_APP_URL ? `${import.meta.env.VITE_APP_URL}/image/${article.image}` : null;

    const handleSelectFirst = () => {
      if (count !== 0) return;
      setSelection({ [article.id]: 1 });
    };

    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={handleSelectFirst}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelectFirst();
          }
        }}
        sx={{
          flex: 1,
          minWidth: 200,
          maxWidth: 200,
          height: CARD_H,
          display: "flex",
          flexDirection: "column",
          borderRadius: 1,
          border: "0.75px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
          cursor: "pointer",
          userSelect: "none",
          "&:active": { bgcolor: "action.hover" },
        }}
      >
        {/* Image area takes the remaining height after label/controls */}
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            bgcolor: "background.default",
          }}
        >
          {imageUrl ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundImage: `url(${imageUrl})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "cover",
              }}
            />
          ) : null}
        </Box>

        {/* Label bar */}
        <Box
          sx={{
            height: LABEL_H,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
            borderTop: "0.75px solid",
            borderTopColor: "divider",
            bgcolor: count > 0 ? "text.primary" : "action.hover",
          }}
        >
          <Typography
            sx={{
              textAlign: "center",
              lineHeight: "12px",
              fontSize: 12,
              color: count > 0 ? "background.paper" : "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {article?.name}
          </Typography>
        </Box>

        {/* Controls row */}
        {count > 0 ? (
          <Box sx={{ height: CONTROLS_H, display: "flex" }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setSelection({ [article.id]: count - 1 });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelection({ [article.id]: count - 1 });
                }
              }}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#a40000ff",
                borderTop: "0.75px solid",
                borderTopColor: "divider",
                borderRight: "0.75px solid",
                borderRightColor: "divider",
                cursor: "pointer",
                "&:active": { bgcolor: "grey.600" },
              }}
            >
              <RemoveIcon sx={{ color: "background.paper" }} />
            </Box>

            <Box
              sx={{
                minWidth: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
                bgcolor: "grey.200",
                borderTop: "0.75px solid",
                borderTopColor: "divider",
              }}
            >
              <Typography sx={{ color: "text.primary", fontSize: 14 }}>{count}</Typography>
            </Box>

            <Box
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setSelection({ [article.id]: count + 1 });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelection({ [article.id]: count + 1 });
                }
              }}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#0e9100ff",
                borderTop: "0.75px solid",
                borderTopColor: "divider",
                borderLeft: "0.75px solid",
                borderLeftColor: "divider",
                cursor: "pointer",
                "&:active": { bgcolor: "grey.600" },
              }}
            >
              <AddIcon sx={{ color: "background.paper" }} />
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  },
);

export default ArticleCard;
